import { collection, deleteField, doc, getDocs, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '@/config/firebase';
import { CandidatoCreateSchema } from '../models/candidato.schemas';
import type { CandidatoCreate } from '../models/candidato.types';
import {
  CAMPOS_PRIVADOS_CANDIDATO,
  COLECAO_CANDIDATOS,
  COLECAO_CANDIDATOS_PRIVADO,
  camposPrivadosDoAberto,
  temCamposPrivadosNoAberto,
} from '../models/candidato.privado';

export interface DocCandidato {
  id: string;
  dados: Record<string, unknown>;
}

export interface CandidatoUpdate {
  id: string;
  nome: string;
  email: string;
}

type ModoEnvioLink = 'resend_email' | 'copy_link';

/**
 * Operações do painel de Recrutamento — o candidato é dividido em dois documentos (LGPD, ver
 * models/candidato.privado.ts): `recruitment_candidatos` (aberto) e
 * `recruitment_candidatos_privado` (protegido), sempre gravados juntos. O recrutador só
 * cadastra nome/e-mail e lista os candidatos; gerar o link/enviar e-mail e receber o
 * preenchimento do próprio candidato passam pelas Cloud Functions (ver
 * useCandidatoMutations), que usam Admin SDK e não ficam sujeitas às regras do cliente.
 */
class CandidatoServiceClass {
  private aberto(id: string) {
    return doc(db, COLECAO_CANDIDATOS, id);
  }

  private privado(id: string) {
    return doc(db, COLECAO_CANDIDATOS_PRIVADO, id);
  }

  async create(data: CandidatoCreate): Promise<string> {
    const validated = CandidatoCreateSchema.parse(data);
    const id = doc(collection(db, COLECAO_CANDIDATOS)).id;
    const lote = writeBatch(db);
    lote.set(this.aberto(id), {
      nome: validated.nome,
      perguntasSobreIA: validated.perguntasSobreIA,
      createdBy: validated.createdBy,
      status: 'aguardando_envio',
      preenchidoEm: null,
      preenchidoEtapa2Em: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    lote.set(this.privado(id), {
      email: validated.email,
      respostas: null,
      respostasEtapa2: null,
      linkEnviadoEm: null,
      linkEtapa2EnviadoEm: null,
      updatedAt: serverTimestamp(),
    });
    await lote.commit();
    return id;
  }

  /** Corrige nome/e-mail cadastrados errado — não mexe em status/respostas/link. */
  async atualizar(data: CandidatoUpdate): Promise<void> {
    const { id, nome, email } = data;
    const lote = writeBatch(db);
    lote.update(this.aberto(id), { nome: nome.trim(), updatedAt: serverTimestamp() });
    lote.set(this.privado(id), { email: email.trim(), updatedAt: serverTimestamp() }, { merge: true });
    await lote.commit();
  }

  /** Marcado manualmente pelo recrutador (aceita/recusada/de volta pra pendente, ver
   * AlternarDecisaoPropostaButtons) — sem integração com Propostas ainda (ver comentário
   * em StatusCandidato). `proposta_aceita` é o que libera a geração do link da Etapa 2. */
  async marcarDecisaoProposta(id: string, status: 'preenchido' | 'proposta_aceita' | 'proposta_recusada'): Promise<void> {
    const lote = writeBatch(db);
    lote.update(this.aberto(id), { status, updatedAt: serverTimestamp() });
    await lote.commit();
  }

  async excluir(id: string): Promise<void> {
    const lote = writeBatch(db);
    lote.delete(this.aberto(id));
    lote.delete(this.privado(id));
    await lote.commit();
  }

  /** Documentos abertos (nome, status, datas) — qualquer um com role. */
  async listar(): Promise<DocCandidato[]> {
    const snap = await getDocs(query(collection(db, COLECAO_CANDIDATOS), orderBy('createdAt', 'desc')));
    return snap.docs.map((d) => ({ id: d.id, dados: d.data() }));
  }

  /** Documentos protegidos — só Recrutamento, Chamados DP e Administrar SI (a regra recusa os demais). */
  async listarPrivados(): Promise<Map<string, Record<string, unknown>>> {
    const snap = await getDocs(collection(db, COLECAO_CANDIDATOS_PRIVADO));
    return new Map(snap.docs.map((d) => [d.id, d.data()]));
  }

  /**
   * Move pro protegido o que ainda está no aberto (candidatos gravados antes da separação) — roda
   * sozinho quando alguém com Recrutamento abre a lista (só ele pode gravar). O protegido que já
   * existe vence (merge só com o que faltar). Retorna quantos foram migrados.
   */
  async migrarCamposPrivados(abertos: DocCandidato[], privados: Map<string, Record<string, unknown>>): Promise<number> {
    const pendentes = abertos.filter((c) => temCamposPrivadosNoAberto(c.dados));
    for (let i = 0; i < pendentes.length; i += 200) {
      const lote = writeBatch(db);
      for (const candidato of pendentes.slice(i, i + 200)) {
        const existentes = privados.get(candidato.id) ?? {};
        const mover = Object.fromEntries(
          Object.entries(camposPrivadosDoAberto(candidato.dados)).filter(([campo]) => !(campo in existentes))
        );
        lote.set(this.privado(candidato.id), { ...mover, updatedAt: serverTimestamp() }, { merge: true });
        lote.update(
          this.aberto(candidato.id),
          Object.fromEntries(CAMPOS_PRIVADOS_CANDIDATO.filter((c) => c in candidato.dados).map((c) => [c, deleteField()]))
        );
      }
      await lote.commit();
    }
    return pendentes.length;
  }

  /** Gera (ou reaproveita) o token de link — `modo: 'resend_email'` (padrão) também
   * dispara o e-mail; `'copy_link'` só devolve o link, sem mandar nada (ver
   * functions/src/recruitment/enviarLinkCandidato.ts). Manda a própria origem
   * (window.location.origin) pra function montar o link certo (dev x produção) — mesmo
   * padrão já usado em manageEvaluationInvite/EvaluationDetailsPage. `ator` é só pra
   * registrar quem clicou (linkEnviadoPor/linkCopiadoPor, mostrado no tooltip do ícone). */
  async enviarLink(
    candidatoId: string,
    modo: ModoEnvioLink = 'resend_email',
    ator?: { id: string; name: string }
  ): Promise<{ link: string }> {
    const functions = getFunctions(app, 'us-central1');
    const enviarLinkCandidato = httpsCallable<
      { candidatoId: string; origin: string; modo: ModoEnvioLink; atorId?: string; atorNome?: string },
      { link: string }
    >(functions, 'enviarLinkCandidato');
    const result = await enviarLinkCandidato({
      candidatoId,
      origin: window.location.origin,
      modo,
      atorId: ator?.id,
      atorNome: ator?.name,
    });
    return result.data;
  }

  /** Igual a `enviarLink`, mas pra Etapa 2 (Cloud Function separada, só libera depois de
   * `proposta_aceita` — ver enviarLinkCandidatoEtapa2). */
  async enviarLinkEtapa2(
    candidatoId: string,
    modo: ModoEnvioLink = 'resend_email',
    ator?: { id: string; name: string }
  ): Promise<{ link: string }> {
    const functions = getFunctions(app, 'us-central1');
    const enviarLinkCandidatoEtapa2 = httpsCallable<
      { candidatoId: string; origin: string; modo: ModoEnvioLink; atorId?: string; atorNome?: string },
      { link: string }
    >(functions, 'enviarLinkCandidatoEtapa2');
    const result = await enviarLinkCandidatoEtapa2({
      candidatoId,
      origin: window.location.origin,
      modo,
      atorId: ator?.id,
      atorNome: ator?.name,
    });
    return result.data;
  }
}

export const candidatoService = new CandidatoServiceClass();
